<template>
  <div
    class="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3"
  >
    <HeroSearchBar v-model="searchQuery" />
    <div class="flex items-center gap-2">
      <label class="text-sm text-gray-700 font-medium">Show</label>
      <select
        v-model.number="itemsPerPage"
        @change="handlePerPageChange"
        class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-ds-button-color"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
      <span class="text-sm text-gray-600">entries</span>
    </div>
  </div>
  <div class="overflow-x-auto">
    <table class="w-full text-sm text-gray-700">
      <thead class="sticky top-0 z-10 bg-gray-50 shadow-sm">
        <tr class="text-left text-xs tracking-wider text-gray-600 uppercase">
          <th class="px-4 sm:px-6 py-3">Applicant Name</th>
          <th class="px-4 sm:px-6 py-3">Broker</th>
          <th class="px-4 sm:px-6 py-3">Designation</th>
          <th class="px-4 sm:px-6 py-3">Application Date</th>
          <th class="px-4 sm:px-6 py-3 text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(agent, index) in agents"
          :key="index"
          class="border-b transition last:border-0 odd:bg-white even:bg-gray-50 hover:bg-gray-100"
        >
          <td class="px-4 sm:px-6 py-4 font-semibold text-gray-900">
            {{ toTitleCase(agent.name) }}
          </td>
          <td class="px-4 sm:px-6 py-4 font-semibold">
            {{ agent.brokername || "N/A" }}
          </td>
          <td class="px-4 sm:px-6 py-4 font-semibold text-gray-700">
            {{ agent.designation || "N/A" }}
          </td>
          <td class="px-4 sm:px-6 py-4 font-semibold text-gray-700">
            {{ agent.formattedDate }}
          </td>
          <td class="px-4 sm:px-6 py-4 font-semibold text-center">
            <button
              @click="openModal(agent)"
              class="rounded-xl bg-ds-button-color px-3 sm:px-5 py-2 text-sm sm:text-base font-semibold text-white shadow-md transition hover:scale-105 active:scale-95"
            >
              View Details
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="isLoading">
      <div
        v-for="n in 1"
        :key="n"
        class="animate-pulse flex justify-between py-4 border-b p-4"
      >
        <div class="h-4 bg-gray-200 rounded w-1/3"></div>
        <div class="h-4 bg-gray-200 rounded w-1/4"></div>
        <div class="h-4 bg-gray-200 rounded w-1/5"></div>
      </div>
    </div>
    <HeroApplicantDetails
      v-if="showModal && userRole === 'staff'"
      :isVisible="showModal"
      :applicant="selectedApplicant"
      @close="closeModal"
      @hired="handleHired"
    />

    <HeroAgentDetails
      v-if="showModal && userRole !== 'staff'"
      :isVisible="showModal"
      :agent="selectedAgent"
      @close="closeModal"
    />
    <HeroSubPagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      @update:page="handlePageChange"
    />
  </div>
</template>

<script>
import { ref, onMounted, inject, computed, watch } from "vue";
import moment from "moment";
import HeroSearchBar from "@/components/common/HeroSearchBar.vue";
import HeroApplicantDetails from "@/components/common/HeroApplicantDetails.vue";
import HeroSubPagination from "@/components/common/HeroSubPagination.vue";
import { toTitleCase } from "@/utils/toTitleCase.js";

export default {
  components: {
    HeroSearchBar,
    HeroApplicantDetails,
    HeroSubPagination,
  },
  emits: ["update-count"],
  setup(props, { emit }) {
    const axios = inject("$axios");
    const searchQuery = ref("");
    const showModal = ref(false);
    const agents = ref([]);
    const perPageOptions = [10, 30, 50];
    const itemsPerPage = ref(10);
    const currentPage = ref(1);
    const totalItems = ref(0);
    const isLoading = ref(false);
    const selectedApplicant = ref(null);

    const fetchAgents = async () => {
      isLoading.value = true;
      try {
        const response = await axios.get("/wb/agent", {
          params: {
            page: currentPage.value,
            pageSize: itemsPerPage.value,
            search: searchQuery.value?.trim() || "",
            sortBy: "agentname",
            sortOrder: "desc",
          },
        });

        const data = response.data;

        agents.value = (data.data || []).map((agent) => ({
          ...agent,
          formattedDate: moment(agent.dtAdded)
            .utcOffset(8)
            .format("dddd, MMMM DD, YYYY"),
        }));

        totalItems.value = data.total || 0;
        emit("update-count", totalItems.value);
      } catch (error) {
        console.error("Error fetching agents:", error);
      } finally {
        isLoading.value = false;
      }
    };

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(totalItems.value / itemsPerPage.value))
    );
    const handlePageChange = (page) => {
      currentPage.value = page;
      fetchAgents();
    };
    const handlePerPageChange = () => {
      currentPage.value = 1;
      fetchAgents();
    };
    const openModal = (agent) => {
      selectedApplicant.value = agent;
      showModal.value = true;
    };
    const closeModal = () => {
      showModal.value = false;
    };

    const handleHired = (applicantId) => {
      showModal.value = false;
      agents.value = agents.value.filter((agent) => agent.id !== applicantId);
      totalItems.value -= 1;
      emit("update-count", -1);
    };

    let searchTimeout;
    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        fetchAgents(1);
      }, 500);
    });

    onMounted(fetchAgents);

    return {
      agents,
      searchQuery,
      showModal,
      openModal,
      closeModal,
      perPageOptions,
      itemsPerPage,
      currentPage,
      handlePageChange,
      handlePerPageChange,
      toTitleCase,
      selectedApplicant,
      isLoading,
      totalItems,
      totalPages,
      handleHired,
    };
  },
};
</script>
